// src/pages/CourseTeachers.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, ArrowLeft, Loader, User, ChevronRight, FileText } from 'lucide-react';
import { publicAPI } from '../services/api';
import Header from '../components/common/Header';

const CourseTeachers = () => {
  const { schoolId, departmentId, year, courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Get Course Info
        const courseRes = await publicAPI.getCourseById(courseId);
        if (courseRes.success) setCourse(courseRes.data);

        // 2. Get All Materials for Course -> Extract Teachers
        const matRes = await publicAPI.getMaterials({ course: courseId });
        
        if (matRes.success && matRes.data.length > 0) {
          const uniqueTeachers = {};
          
          matRes.data.forEach(mat => {
            if (mat.teacher) {
              const tId = mat.teacher._id || mat.teacher.id;
              if (!uniqueTeachers[tId]) {
                uniqueTeachers[tId] = {
                  ...mat.teacher,
                  materialCount: 0
                };
              }
              uniqueTeachers[tId].materialCount += 1;
            }
          });
          setTeachers(Object.values(uniqueTeachers));
        } else {
            setTeachers([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center">
        <Loader className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />
      
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to={`/school/${schoolId}/department/${departmentId}/year/${year}`} className="text-blue-200 hover:text-white flex items-center gap-2 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4"/> Back to Courses
          </Link>
          <h1 className="text-3xl font-bold mb-2">{course?.title}</h1>
          <p className="text-blue-200 text-lg">{course?.courseCode} • Select an Instructor</p>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8">
        {teachers.length === 0 ? (
          <div className="bg-white p-10 rounded-xl shadow-lg text-center">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Materials Yet</h3>
            <p className="text-gray-500">No instructors have uploaded materials for this course yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teachers.map((teacher) => (
              <div 
                key={teacher._id}
                onClick={() => navigate(`/school/${schoolId}/department/${departmentId}/year/${year}/course/${courseId}/teacher/${teacher._id}`)}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100 cursor-pointer transition-all duration-300 group hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center text-blue-700 font-bold text-xl shadow-inner overflow-hidden border-2 border-white">
                    {teacher.showAvatarPublicly !== false && teacher.profileImage?.url ? (
                      <img 
                        src={publicAPI.getFullImageUrl(teacher.profileImage.url)}
                        alt={teacher.fullName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = teacher.fullName ? teacher.fullName.charAt(0).toUpperCase() : '?';
                        }}
                      />
                    ) : (
                      teacher.fullName ? teacher.fullName.charAt(0).toUpperCase() : <User />
                    )}
                  </div>
                  <div>
                    <h3 className="font-black text-lg text-gray-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight">
                      {teacher.fullName || "Unknown Instructor"}
                    </h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{teacher.email}</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-100 pt-4 flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span>{teacher.materialCount} Materials Uploaded</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseTeachers;